/**
 * Ambient store for per-prescription EPA / Priority Indicator checkbox selections.
 *
 * The summary card's checkboxes live deep in the component tree, while the send
 * payload is assembled by `createPayload` (which already reads ambient context like
 * patientId / sessionId). Rather than thread a callback through several layers, the
 * card writes its selections here and `createPayload` reads them at send time.
 *
 * Keyed by patientId so switching patients (without a full reload) never leaks one
 * patient's choices into another's send. Matching to medication rows is done on the
 * server by normalized medication name.
 *
 * Cleared on logout via a full page reload (module state is recreated), same as the
 * conversation/session stores.
 */

export interface PrescriptionFlags {
  priorAuthorization: boolean;
  priorityIndicator: boolean;
  epcsQueued: boolean;
}

type ByName = Record<string, PrescriptionFlags>;

let store: Record<string, ByName> = {};

const normName = (name: string): string =>
  (name || "").trim().replace(/\s+/g, " ").toLowerCase();

/** Record the EPA / Priority Indicator selection for one medication (by name) under a patient. */
export const setPrescriptionFlag = (
  patientId: string,
  medicationName: string,
  flags: PrescriptionFlags
): void => {
  const p = patientId || "";
  const key = normName(medicationName);
  if (!key) return;
  store[p] = store[p] || {};
  store[p][key] = { ...flags };
};

/** Return the flags map (name -> flags) for a patient, or an empty object. */
export const getPrescriptionFlags = (patientId: string): ByName =>
  store[patientId || ""] || {};

/** Drop all stored selections (e.g. on logout / session reset). */
export const clearPrescriptionFlags = (): void => {
  store = {};
};
